import { useEffect, useState } from 'react';
import { Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import type { MotivoAlDescartar, MotivoDeDescarte } from '../types/price-proposals.types';
import { EncabezadoDeVentana, PIE_DE_VENTANA } from '@/features/compite/ui/ventana-compite';

/**
 * Descartar con motivo (lote B1, spec 246 punto 3, D2; maqueta `b1-descartar`):
 * seis opciones de un toque y un texto libre, los dos opcionales. «Otro» pide
 * el texto. El mismo motivo vale para todas las elegidas.
 *
 * Por qué estas seis: son las razones por las que una persona dice que no a
 * una propuesta que la cuenta da por buena. Las dos primeras son datos que hay
 * que arreglar: contarlas dice dónde fallan los datos.
 */

/** Las seis, en el orden de la maqueta. */
export const MOTIVOS_DE_DESCARTE: Array<{ clave: MotivoDeDescarte; texto: string }> = [
  { clave: 'DATO_DEL_MERCADO', texto: 'El dato del mercado está mal' },
  { clave: 'COSTO_MAL_CARGADO', texto: 'El costo está mal cargado' },
  { clave: 'PROMOCION_O_ACUERDO', texto: 'Hay una promoción o un acuerdo' },
  { clave: 'LO_CAMBIO_A_MANO', texto: 'Lo cambio yo a mano' },
  { clave: 'PREFIERO_ESPERAR', texto: 'Prefiero esperar' },
  { clave: 'OTRO', texto: 'Otro' },
];

export function DialogoDescartar({
  cuantas,
  abierto,
  onCerrar,
  onDescartar,
}: {
  /** Cuántas se descartan con este motivo. */
  cuantas: number;
  abierto: boolean;
  onCerrar: () => void;
  /** Descarta; la ventana se cierra cuando termina. */
  onDescartar: (motivo: MotivoAlDescartar) => Promise<void>;
}) {
  const [motivo, setMotivo] = useState<MotivoDeDescarte | null>(null);
  const [nota, setNota] = useState('');
  const [trabajando, setTrabajando] = useState(false);

  // Cada vez que se abre, empieza en blanco.
  useEffect(() => {
    if (abierto) {
      setMotivo(null);
      setNota('');
    }
  }, [abierto]);

  const faltaTexto = motivo === 'OTRO' && nota.trim() === '';
  const una = cuantas === 1;
  const lasN = cuantas === 2 ? 'las dos' : `las ${formatoCantidad(cuantas)}`;

  async function descartar() {
    setTrabajando(true);
    try {
      await onDescartar({ motivo, nota: nota.trim() || null });
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && !trabajando && onCerrar()}>
      <DialogContent className="max-w-xl">
        <EncabezadoDeVentana icono={XCircle}>
          <DialogTitle>
            {una ? 'Descartar 1 propuesta' : `Descartar ${formatoCantidad(cuantas)} propuestas`}
          </DialogTitle>
          <DialogDescription>
            {una
              ? 'Ningún precio se mueve. No vuelve a aparecer mientras el precio propuesto siga casi igual.'
              : 'Ningún precio se mueve. No vuelven a aparecer mientras el precio propuesto siga casi igual.'}
          </DialogDescription>
        </EncabezadoDeVentana>

        <div className="space-y-2 text-sm">
          <p className="font-medium">
            ¿Por qué?{' '}
            <span className="font-normal text-muted-foreground">
              Opcional: sirve para que quien venga después lo entienda.
            </span>
          </p>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Motivo">
            {MOTIVOS_DE_DESCARTE.map((m) => {
              const elegido = motivo === m.clave;
              return (
                <button
                  key={m.clave}
                  type="button"
                  aria-pressed={elegido}
                  onClick={() => setMotivo(elegido ? null : m.clave)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-sm',
                    elegido
                      ? 'border-primary bg-primary/10 font-semibold text-primary'
                      : 'text-muted-foreground hover:border-foreground/40 hover:text-foreground'
                  )}
                >
                  {m.texto}
                </button>
              );
            })}
          </div>

          <label className="block pt-2 font-medium" htmlFor="motivo-con-tus-palabras">
            Con tus palabras{' '}
            <span className="font-normal text-muted-foreground">
              {motivo === 'OTRO' ? '(con «Otro», hace falta)' : '(opcional)'}
            </span>
          </label>
          <Textarea
            id="motivo-con-tus-palabras"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            maxLength={500}
            className="min-h-[64px]"
            error={faltaTexto}
          />
          <p className={cn('text-xs', faltaTexto ? 'text-alerta' : 'text-muted-foreground')}>
            {faltaTexto
              ? 'Con «Otro», escribí el motivo con tus palabras.'
              : una
                ? 'Se guarda con tu nombre y la fecha.'
                : `Se guarda con tu nombre y la fecha. El mismo motivo vale para ${lasN}.`}
          </p>
        </div>

        <DialogFooter className={PIE_DE_VENTANA}>
          <Button variant="outline" onClick={onCerrar} disabled={trabajando}>
            Cancelar
          </Button>
          <Button
            onClick={() => void descartar()}
            disabled={trabajando || faltaTexto}
            className="gap-1.5"
          >
            {trabajando && <Loader2 className="h-4 w-4 animate-spin" />}
            {`Descartar ${formatoCantidad(cuantas)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
